import { ENV_NAME, getEnvironment } from '@heidi/api/config';
import { Button } from '@heidi/design-system/button';
import {
  SegmentedControl,
  SegmentedControlList,
  SegmentedControlTrigger,
} from '@heidi/design-system/segmented-control';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@heidi/design-system/select';
import { TypographyCaption, TypographyP2SemiBold } from '@heidi/design-system/typography';
import {
  dictateOnboardingDevLayoutOverrideAtom,
  dictateOnboardingDevPlatformOverrideAtom,
  regionToLegend,
  useDictateOnboardingIsMac,
  useKeyboardLayout,
  type DictateOnboardingDevLayoutOverride,
  type DictateOnboardingDevPlatformOverride,
  type KeyboardLayout,
} from '@heidi/dictate';
import { useAtom } from 'jotai';
import { useRouter } from 'next/router';
import { useCallback, useMemo } from 'react';

type PlatformSegmentValue = 'auto' | 'mac' | 'windows';

const PLATFORM_SEGMENTS: { value: PlatformSegmentValue; label: string }[] = [
  { value: 'auto', label: 'Auto' },
  { value: 'mac', label: 'Mac' },
  { value: 'windows', label: 'Windows' },
];

type LayoutSelectValue = 'auto' | KeyboardLayout;

const LAYOUT_OPTIONS: { value: LayoutSelectValue; label: string }[] = [
  { value: 'auto', label: 'Auto (detected)' },
  { value: 'standard', label: 'Standard (QWERTY)' },
  { value: 'france-belgium', label: 'France / Belgium (AZERTY)' },
  { value: 'germany-austria', label: 'Germany / Austria (QWERTZ)' },
  { value: 'switzerland', label: 'Switzerland (QWERTZ)' },
  { value: 'spain', label: 'Spain (QWERTY)' },
  { value: 'italy', label: 'Italy (QWERTY)' },
  { value: 'uk', label: 'UK (QWERTY)' },
  { value: 'japan', label: 'Japan (QWERTY)' },
];

interface DictateOnboardingDevSectionProps {
  onRequestClose?: () => void;
}

export function DictateOnboardingDevSection({
  onRequestClose,
}: Readonly<DictateOnboardingDevSectionProps>) {
  const router = useRouter();
  const [platformOverride, setPlatformOverride] = useAtom(
    dictateOnboardingDevPlatformOverrideAtom
  );
  const [layoutOverride, setLayoutOverride] = useAtom(
    dictateOnboardingDevLayoutOverrideAtom
  );

  const isMacPreview = useDictateOnboardingIsMac();
  const { layout } = useKeyboardLayout();

  const isNonProd = getEnvironment() !== ENV_NAME.PROD;

  const platformSegmentValue: PlatformSegmentValue =
    platformOverride == null ? 'auto' : platformOverride;

  const layoutSelectValue: LayoutSelectValue =
    layoutOverride == null ? 'auto' : layoutOverride;

  const resolvedPlatformLabel = useMemo(
    () => (isMacPreview ? 'macOS' : 'Windows'),
    [isMacPreview]
  );

  const resolvedLegendLabel = useMemo(() => regionToLegend(layout), [layout]);

  const handlePlatformChange = useCallback(
    (next: string) => {
      const value = next as PlatformSegmentValue;
      const override: DictateOnboardingDevPlatformOverride =
        value === 'auto' ? null : value;
      setPlatformOverride(override);
    },
    [setPlatformOverride]
  );

  const handleLayoutChange = useCallback(
    (next: string) => {
      const value = next as LayoutSelectValue;
      const override: DictateOnboardingDevLayoutOverride =
        value === 'auto' ? null : value;
      setLayoutOverride(override);
    },
    [setLayoutOverride]
  );

  if (!isNonProd) {
    return null;
  }

  return (
    <section aria-labelledby="dev-tools-dictate-onboarding-heading" className="mt-4">
      <TypographyP2SemiBold id="dev-tools-dictate-onboarding-heading" className="mb-1">
        Dictate onboarding
      </TypographyP2SemiBold>
      <TypographyCaption className="mb-3 text-text-tertiary">
        Preview the onboarding keyboard illustration and layout without changing OS
        settings. Open the flow to see overrides applied live.
      </TypographyCaption>

      <div className="flex flex-col gap-3">
        <div className="min-w-0 space-y-1">
          <TypographyCaption className="text-text-secondary">Platform</TypographyCaption>
          <TypographyCaption className="text-text-tertiary">
            Resolved: <span className="font-mono">{resolvedPlatformLabel}</span>
            {platformOverride != null ? ' (dev override)' : ''}
          </TypographyCaption>
        </div>
        <SegmentedControl
          value={platformSegmentValue}
          onValueChange={handlePlatformChange}
        >
          <SegmentedControlList className="grid w-full grid-cols-3 gap-1">
            {PLATFORM_SEGMENTS.map((opt) => (
              <SegmentedControlTrigger
                key={opt.value}
                value={opt.value}
                data-testid={`dev-tools-dictate-onboarding-platform-${opt.value}`}
              >
                {opt.label}
              </SegmentedControlTrigger>
            ))}
          </SegmentedControlList>
        </SegmentedControl>

        <div className="min-w-0 space-y-1">
          <TypographyCaption className="text-text-secondary">
            Keyboard region / layout
          </TypographyCaption>
          <TypographyCaption className="text-text-tertiary">
            Resolved: <span className="font-mono">{layout}</span>
            {' · '}
            legend <span className="font-mono">{resolvedLegendLabel}</span>
            {layoutOverride != null ? ' (dev override)' : ''}
          </TypographyCaption>
        </div>
        <Select value={layoutSelectValue} onValueChange={handleLayoutChange}>
          <SelectTrigger
            className="w-full"
            data-testid="dev-tools-dictate-onboarding-layout-select"
          >
            <SelectValue placeholder="Layout" />
          </SelectTrigger>
          {/* z-[10001] sits one above the dev sheet (z-[10000]) so the menu isn't behind it. */}
          <SelectContent className="z-[10001]">
            {LAYOUT_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button
          type="button"
          variant="outline"
          size="sm"
          data-testid="dev-tools-open-dictate-onboarding"
          onClick={() => {
            onRequestClose?.();
            void router.push('/dictate-onboarding');
          }}
        >
          Open dictate onboarding
        </Button>
      </div>
    </section>
  );
}
